import {
  isAxiosError,
  type AxiosHeaders,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ERROR_CODES, INTEGRITY_HEADER } from '@capsico/contracts';

/*
 * W24 app integrity — the in-memory pass and the 403 retry. No imports from client.ts (the client
 * imports this file). The pass is a short-lived, device-bound signature (6 h); it stays in memory
 * only and is fetched again on every start.
 */

export type IntegrityStatus =
  /** Not checked yet. */
  | 'idle'
  /** The server has integrity off (or the feature is not deployed): the app does nothing. */
  | 'off'
  /** Passed (or Google was unreachable, which is allowed). */
  | 'ok'
  /** A browser or an iOS build: no Play Integrity here (iOS App Attest is dormant). */
  | 'unsupported'
  /** Enforce mode and this copy of the app could not be verified. */
  | 'failed';

let pass: string | null = null;
let passExpiresAt = 0;
let status: IntegrityStatus = 'idle';
const listeners = new Set<() => void>();

type Refresher = (reason: 'required' | 'failed') => Promise<boolean>;
let refresher: Refresher | null = null;

export function integrityStatus(): IntegrityStatus {
  return status;
}

export function setIntegrityStatus(next: IntegrityStatus): void {
  if (next === status) return;
  status = next;
  for (const listener of listeners) listener();
}

export function subscribeIntegrity(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function setIntegrityPass(next: string | null, expiresAt: string | null): void {
  pass = next;
  passExpiresAt = next && expiresAt ? Date.parse(expiresAt) : 0;
}

/** Milliseconds until the pass expires (0 when there is none). */
export function passRemainingMs(now = Date.now()): number {
  return pass ? Math.max(0, passExpiresAt - now) : 0;
}

/** Request side: send the pass while it is valid. */
export function attachIntegrity(headers: AxiosHeaders): void {
  if (pass && Date.now() < passExpiresAt) headers.set(INTEGRITY_HEADER, pass);
}

/** The integrity module registers how to get a fresh pass (single flight). */
export function setIntegrityRefresher(next: Refresher | null): void {
  refresher = next;
}

type RetryConfig = InternalAxiosRequestConfig & { _capsicoIntegrityRetried?: boolean };

/**
 * Response side: a 403 "integrity_required" / "integrity_failed" from a sensitive route gets one
 * fresh check and one retry. The retry reuses the same X-Idempotency-Key, so a write is never
 * applied twice.
 */
export function integrityRetry(instance: AxiosInstance) {
  return async (error: unknown) => {
    if (!isAxiosError(error) || error.response?.status !== 403 || !refresher) throw error;
    const body = error.response.data as { error?: { code?: unknown } } | undefined;
    const code = body?.error?.code;
    if (code !== ERROR_CODES.INTEGRITY_REQUIRED && code !== ERROR_CODES.INTEGRITY_FAILED) {
      throw error;
    }
    const config = error.config as RetryConfig | undefined;
    if (
      !config ||
      config._capsicoIntegrityRetried ||
      (config.url ?? '').startsWith('/v1/integrity/')
    ) {
      throw error;
    }
    const ok = await refresher(code === ERROR_CODES.INTEGRITY_FAILED ? 'failed' : 'required');
    if (!ok) throw error;
    config._capsicoIntegrityRetried = true;
    return instance.request(config);
  };
}
